import { type FC, type RefObject, useMemo, useRef, useState } from 'react'
import { Dropdown, Form, Placeholder } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../../../components/Button/Button'
import { Modal } from '../../../../../components/Modal/Modal'
import { useAppSelector } from '../../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useGetEventTypeHostCandidatesQuery } from '../../../../../store/scheduling/scheduling'
import { HostRow } from './HostRow'
import { type HostPerson, hostDisplayName } from './hostPeople'
import styles from './BookingPages.module.scss'

const PAGE_SIZE = 6

export type HostKind = 'hosts' | 'co-hosts'

export type ManagedHost = HostPerson & { required?: boolean }

const KIND_COPY: Record<HostKind, { title: string; plural: string }> = {
  hosts: { title: 'Choose hosts', plural: 'hosts' },
  'co-hosts': { title: 'Choose co-hosts', plural: 'co-hosts' },
}

const ATTENDANCE_OPTIONS = [
  { required: true, label: 'Required', hint: 'Must attend the meeting.' },
  { required: false, label: 'Optional', hint: 'Gets the invite. Optional to attend.' },
]

const attendanceLabel = (required: boolean): string => (required ? 'Required' : 'Optional')

const selectionHint = (kind: HostKind, count: number): string => {
  if (kind === 'co-hosts') {
    return count === 0 ? 'Co-hosts join every meeting.' : '“Required” co-hosts must attend.'
  }
  if (count === 0) {
    return 'Pick 2 or more and they take turns.'
  }
  return count === 1 ? 'One host takes every meeting.' : '2 or more take turns.'
}

const peopleSnapshot = (people: ManagedHost[]): string => JSON.stringify(people.map((person) => `${person.userId}:${person.required === true}`).sort())

const matchesTerm = (person: HostPerson, term: string): boolean => `${person.name ?? ''} ${person.email ?? ''}`.toLowerCase().includes(term.toLowerCase())

const byName = (first: HostPerson, second: HostPerson): number => hostDisplayName(first).localeCompare(hostDisplayName(second))

interface AttendanceSelectProps {
  name: string
  required: boolean
  disabled: boolean
  onChange: (required: boolean) => void
}

const AttendanceSelect: FC<AttendanceSelectProps> = ({ name, required, disabled, onChange }) => (
  <Dropdown>
    <Dropdown.Toggle
      as={Button}
      bsPrefix='attendance-toggle'
      variant='grayGhost'
      disabled={disabled}
      aria-label={`Attendance for ${name}`}
      className={classNames('d-flex align-items-center justify-content-between zocks-px-0.75 py-0 button-medium fw-medium', styles.attendanceToggle)}
    >
      {attendanceLabel(required)}
      <i className='bi bi-chevron-expand' aria-hidden='true' />
    </Dropdown.Toggle>
    <Dropdown.Menu align='end' className={classNames('zocks-p-0.375 border zocks-border-color-gray-300 rounded-3 shadow', styles.attendanceMenu)}>
      <div className='d-flex flex-column zocks-gap-0.125'>
        {ATTENDANCE_OPTIONS.map((option) => {
          const isSelected = option.required === required
          return (
            <Dropdown.Item
              key={option.label}
              as='button'
              type='button'
              className={classNames('d-flex align-items-start zocks-gap-0.75 py-2 rounded-2 text-nowrap', styles.attendanceOption, {
                'zocks-bg-color-primary-light': isSelected,
              })}
              onClick={() => onChange(option.required)}
            >
              <span className='d-flex flex-column flex-grow-1 zocks-gap-0.125'>
                <span className={classNames('paragraph-medium fw-semibold', isSelected ? 'zocks-text-primary' : 'zocks-text-gray-900')}>{option.label}</span>
                <span className='paragraph-small zocks-text-gray-600'>{option.hint}</span>
              </span>
              {isSelected ? <i className='bi bi-check-lg zocks-text-primary' aria-hidden='true' /> : null}
            </Dropdown.Item>
          )
        })}
      </div>
    </Dropdown.Menu>
  </Dropdown>
)

interface SearchFieldProps {
  placeholder: string
  value: string
  inputRef: RefObject<HTMLInputElement>
  onChange: (value: string) => void
}

const SearchField: FC<SearchFieldProps> = ({ placeholder, value, inputRef, onChange }) => (
  <div className={classNames('d-flex align-items-center zocks-gap-0.75 w-100 border rounded-1 zocks-border-color-gray-500 zocks-px-0.75', styles.search)}>
    <i className='bi bi-search zocks-text-gray-900' aria-hidden='true' />
    <Form.Control
      ref={inputRef}
      type='search'
      autoComplete='off'
      autoFocus={!window.matchMedia?.('(pointer: coarse)').matches}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      className={classNames('border-0 p-0 shadow-none paragraph-medium', styles.searchInput)}
    />
  </div>
)

interface ListMessageProps {
  title: string
  hint: string
  actionLabel: string
  onAction: () => void
}

const ListMessage: FC<ListMessageProps> = ({ title, hint, actionLabel, onAction }) => (
  <div className='d-flex flex-column flex-grow-1 align-items-center justify-content-center zocks-gap-0.25 text-center p-4'>
    <span className='paragraph-medium fw-semibold'>{title}</span>
    <span className={classNames('zocks-text-gray-600', styles.secondaryText)}>{hint}</span>
    <Button type='button' variant='link' className='p-0 button-medium text-decoration-none zocks-text-primary' onClick={onAction}>
      {actionLabel}
    </Button>
  </div>
)

const SkeletonRows: FC = () => (
  <div aria-hidden='true' className={classNames('d-flex flex-column', styles.skeleton)}>
    {Array.from({ length: PAGE_SIZE }, (_, index) => (
      <div
        key={index}
        className={classNames(
          'd-flex flex-wrap flex-sm-nowrap align-items-center zocks-py-0.75 column-gap-4 row-gap-2 border-bottom zocks-border-color-gray-300',
          styles.hostRow,
        )}
      >
        <Placeholder as='span' animation='glow'>
          <Placeholder className={classNames('d-block rounded-1', styles.skeletonBox)} />
        </Placeholder>
        <Placeholder as='span' animation='glow' className='d-flex flex-column zocks-gap-0.375 flex-grow-1'>
          <Placeholder xs={4} className='rounded-2' />
          <Placeholder xs={5} size='sm' className='rounded-2' />
        </Placeholder>
      </div>
    ))}
  </div>
)

interface FilterTabProps {
  label: string
  isActive: boolean
  onClick: () => void
}

const FilterTab: FC<FilterTabProps> = ({ label, isActive, onClick }) => (
  <button
    type='button'
    aria-pressed={isActive}
    className={classNames(
      'rounded-2',
      styles.filterTab,
      isActive ? 'zocks-bg-color-white zocks-border-color-gray-300 zocks-text-gray-900 fw-bold' : 'bg-transparent zocks-text-gray-600 fw-medium',
    )}
    onClick={onClick}
  >
    {label}
  </button>
)

interface CounterLabelInput {
  term: string
  isSelectedTab: boolean
  selectedCount: number
  pageCount: number
  firstIndex: number
  visibleCount: number
  shownCount: number
}

const counterLabelFor = ({ term, isSelectedTab, selectedCount, pageCount, firstIndex, visibleCount, shownCount }: CounterLabelInput): string => {
  if (term) {
    return `${shownCount} ${shownCount === 1 ? 'result' : 'results'}`
  }
  if (isSelectedTab) {
    return `${selectedCount} selected`
  }
  if (pageCount > 1) {
    return `${firstIndex + 1}–${firstIndex + visibleCount} of ${shownCount}`
  }
  return `${shownCount} ${shownCount === 1 ? 'person' : 'people'}`
}

interface CandidateListProps {
  kind: HostKind
  rows: ManagedHost[]
  selection: Map<string, boolean>
  hostIds: string[]
  disabled: boolean
  isSaving: boolean
  nameOf: (person: HostPerson) => string
  onToggle: (userId: string) => void
  onAttendanceChange: (userId: string, required: boolean) => void
}

const CandidateList: FC<CandidateListProps> = ({ kind, rows, selection, hostIds, disabled, isSaving, nameOf, onToggle, onAttendanceChange }) => (
  <div role='list' className='d-flex flex-column'>
    {rows.map((row) => {
      const isChecked = selection.has(row.userId)
      const required = selection.get(row.userId) ?? true
      return (
        <HostRow
          key={row.userId}
          host={row}
          displayName={nameOf(row)}
          isHost={kind === 'co-hosts' && hostIds.includes(row.userId)}
          leading={
            <Form.Check
              type='checkbox'
              id={`${kind}-${row.userId}`}
              className='flex-shrink-0'
              checked={isChecked}
              disabled={disabled || isSaving}
              onChange={() => onToggle(row.userId)}
              aria-label={nameOf(row)}
            />
          }
          action={
            kind === 'co-hosts' && isChecked ? (
              disabled ? (
                <span className='paragraph-medium'>{attendanceLabel(required)}</span>
              ) : (
                <AttendanceSelect
                  name={hostDisplayName(row)}
                  required={required}
                  disabled={isSaving}
                  onChange={(next) => onAttendanceChange(row.userId, next)}
                />
              )
            ) : undefined
          }
        />
      )
    })}
  </div>
)

interface SelectionSummaryProps {
  kind: HostKind
  isLoading: boolean
  isError: boolean
  selectedCount: number
  hiddenCount: number
  hasCalendarWarning: boolean
}

const SelectionSummary: FC<SelectionSummaryProps> = ({ kind, isLoading, isError, selectedCount, hiddenCount, hasCalendarWarning }) => {
  if (isLoading) {
    return <span className='paragraph-small zocks-text-gray-600'>Loading people…</span>
  }
  if (isError) {
    return null
  }
  if (hiddenCount > 0) {
    return (
      <span className='paragraph-small zocks-text-gray-600'>
        {selectedCount} selected · <span className='text-nowrap'>{hiddenCount} hidden by the search</span>
      </span>
    )
  }
  if (hasCalendarWarning) {
    return (
      <span className='paragraph-small zocks-text-warning-text'>
        {selectedCount} selected · <span className='text-nowrap'>Calendar not connected</span>
      </span>
    )
  }
  return (
    <span className='paragraph-small zocks-text-gray-600'>
      {selectedCount} selected · <span className='text-nowrap'>{selectionHint(kind, selectedCount)}</span>
    </span>
  )
}

interface ManageHostsModalProps<T extends ManagedHost> {
  kind: HostKind
  eventTypeId: string
  people: T[]
  hostIds?: string[]
  disabled: boolean
  toEntry: (person: HostPerson) => T
  onDone: (people: T[]) => Promise<unknown>
  onCancel: () => void
}

export const ManageHostsModal = <T extends ManagedHost>({
  kind,
  eventTypeId,
  people,
  hostIds = [],
  disabled,
  toEntry,
  onDone,
  onCancel,
}: ManageHostsModalProps<T>) => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const copy = KIND_COPY[kind]
  const [selection, setSelection] = useState<Map<string, boolean>>(() => new Map(people.map((person) => [person.userId, person.required ?? true])))
  const [selectedTabIds, setSelectedTabIds] = useState<string[] | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  const {
    data: candidates,
    isFetching,
    isError,
    refetch,
  } = useGetEventTypeHostCandidatesQuery({ accountId: authenticatedUser.accountId, eventTypeId, suppressGlobalErrorToast: true })
  const isLoading = isFetching && candidates === undefined

  const rows = useMemo<ManagedHost[]>(() => {
    const candidateById = new Map((candidates ?? []).map((candidate) => [candidate.userId, candidate]))
    const savedIds = new Set(people.map((person) => person.userId))
    const saved = people.map((person) => {
      const candidate = candidateById.get(person.userId)
      return { ...candidate, ...person, roleIds: candidate?.roleIds ?? person.roleIds }
    })
    const others = (candidates ?? []).filter((candidate) => !savedIds.has(candidate.userId)).sort(byName)
    return [...saved, ...others]
  }, [candidates, people])

  const isReady = !isLoading && !isError
  const isSelectedTab = selectedTabIds !== null
  const term = search.trim()
  const tabRows = selectedTabIds ? rows.filter((row) => selectedTabIds.includes(row.userId)) : rows
  const shownRows = term ? tabRows.filter((row) => matchesTerm(row, term)) : tabRows
  const pageCount = Math.max(1, Math.ceil(shownRows.length / PAGE_SIZE))
  const currentPage = Math.min(page, pageCount - 1)
  const firstIndex = currentPage * PAGE_SIZE
  const visibleRows = shownRows.slice(firstIndex, firstIndex + PAGE_SIZE)
  const selectedCount = selection.size
  const hiddenCount = term ? Array.from(selection.keys()).filter((userId) => !shownRows.some((row) => row.userId === userId)).length : 0
  const hasCalendarWarning = rows.some((row) => selection.has(row.userId) && row.status === 'active' && !row.eligible)
  const allShownSelected = isReady && shownRows.length > 0 && shownRows.every((row) => selection.has(row.userId))

  const savedById = new Map<string, T>(people.map((person) => [person.userId, person]))
  const withAttendance = (entry: T, required: boolean): T => (kind === 'co-hosts' ? { ...entry, required } : entry)
  const result = rows
    .filter((row) => selection.has(row.userId))
    .map((row) => withAttendance(savedById.get(row.userId) ?? toEntry(row), selection.get(row.userId) ?? true))

  const nameOf = (person: HostPerson) => (person.userId === authenticatedUser.id ? `${hostDisplayName(person)} (You)` : hostDisplayName(person))

  const updateSelection = (update: (next: Map<string, boolean>) => void) => {
    setSelection((current) => {
      const next = new Map(current)
      update(next)
      return next
    })
  }

  const handleToggle = (userId: string) => {
    updateSelection((next) => {
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.set(userId, true)
      }
    })
  }

  const handleSelectAll = () => {
    updateSelection((next) => {
      shownRows.forEach((row) => {
        if (allShownSelected) {
          next.delete(row.userId)
        } else if (!next.has(row.userId)) {
          next.set(row.userId, true)
        }
      })
    })
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(0)
  }

  const clearSearch = () => {
    handleSearchChange('')
    searchRef.current?.focus()
  }

  const showAll = () => {
    setSelectedTabIds(null)
    setPage(0)
  }

  const showSelected = () => {
    setSelectedTabIds(rows.filter((row) => selection.has(row.userId)).map((row) => row.userId))
    setPage(0)
  }

  const handleSave = async () => {
    if (peopleSnapshot(result) === peopleSnapshot(people)) {
      onCancel()
      return
    }
    setIsSaving(true)
    await onDone(result)
    setIsSaving(false)
  }

  const allCountLabel = isLoading ? '…' : isError ? '–' : String(rows.length)
  const counterLabel = counterLabelFor({
    term,
    isSelectedTab,
    selectedCount,
    pageCount,
    firstIndex,
    visibleCount: visibleRows.length,
    shownCount: shownRows.length,
  })

  return (
    <Modal
      show
      onHide={isSaving ? undefined : onCancel}
      dialogClassName={styles.hostsDialog}
      contentClassName={classNames('border-0', styles.hostsContent)}
      header={
        <h4 className='mb-0 d-flex align-items-center zocks-gap-0.75'>
          <i className='bi bi-people-fill' aria-hidden='true' />
          {copy.title}
        </h4>
      }
      headerClassName='py-3 px-4'
      bodyClassName='d-flex flex-column p-0 overflow-hidden'
      body={
        <>
          <div className={classNames('px-4 border-bottom zocks-border-color-gray-300', styles.searchArea)}>
            <SearchField placeholder={`Search ${copy.plural}`} value={search} inputRef={searchRef} onChange={handleSearchChange} />
          </div>
          <div className='d-flex align-items-center justify-content-between zocks-gap-0.75 px-4 py-2 zocks-bg-color-gray-100'>
            <div className='d-flex align-items-center zocks-gap-0.25'>
              <FilterTab label={`All (${allCountLabel})`} isActive={!isSelectedTab} onClick={showAll} />
              <FilterTab label={`Selected (${selectedCount})`} isActive={isSelectedTab} onClick={showSelected} />
            </div>
            {isReady && !(isSelectedTab && !term && shownRows.length === 0) ? (
              <div className='d-flex align-items-center zocks-gap-0.75'>
                <span className={classNames('zocks-text-gray-600 text-nowrap fw-medium', styles.tabText)}>{counterLabel}</span>
                {pageCount > 1 ? (
                  <div className='d-flex align-items-center zocks-gap-0.5'>
                    <Button
                      type='button'
                      variant='grayButton'
                      className={classNames('d-flex align-items-center justify-content-center p-0', styles.pagerButton)}
                      aria-label='Previous page'
                      disabled={currentPage === 0}
                      onClick={() => setPage(currentPage - 1)}
                    >
                      <i className='bi bi-chevron-left' aria-hidden='true' />
                    </Button>
                    <Button
                      type='button'
                      variant='grayButton'
                      className={classNames('d-flex align-items-center justify-content-center p-0', styles.pagerButton)}
                      aria-label='Next page'
                      disabled={currentPage === pageCount - 1}
                      onClick={() => setPage(currentPage + 1)}
                    >
                      <i className='bi bi-chevron-right' aria-hidden='true' />
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className={classNames('d-flex flex-column flex-grow-1 px-4 overflow-auto', styles.scrollArea)}>
            {isLoading ? (
              <SkeletonRows />
            ) : isError ? (
              <ListMessage title='Couldn’t load people' hint='Check your connection and try again.' actionLabel='Try again' onAction={refetch} />
            ) : shownRows.length === 0 && term ? (
              <ListMessage title={`No one matches “${term}”`} hint='Check the spelling or search by email.' actionLabel='Clear search' onAction={clearSearch} />
            ) : shownRows.length === 0 && isSelectedTab ? (
              <ListMessage
                title={`No ${copy.plural} selected`}
                hint={`Select people in “All” to add them as ${copy.plural}.`}
                actionLabel='View all'
                onAction={showAll}
              />
            ) : (
              <CandidateList
                kind={kind}
                rows={visibleRows}
                selection={selection}
                hostIds={hostIds}
                disabled={disabled}
                isSaving={isSaving}
                nameOf={nameOf}
                onToggle={handleToggle}
                onAttendanceChange={(userId, required) => updateSelection((next) => next.set(userId, required))}
              />
            )}
          </div>
        </>
      }
      footerClassName={classNames('align-items-center justify-content-between flex-wrap flex-sm-nowrap zocks-gap-0.75 pt-3 px-4 pb-4', styles.hostsFooter)}
      footer={
        <>
          <div className={classNames('d-flex align-items-center zocks-gap-1', styles.footerSummary)}>
            {disabled ? null : (
              <Button
                type='button'
                variant='link'
                className='p-0 button-medium text-decoration-none zocks-text-primary text-nowrap'
                disabled={!isReady || shownRows.length === 0 || isSaving}
                onClick={handleSelectAll}
              >
                {allShownSelected ? 'Deselect all' : 'Select all'}
              </Button>
            )}
            <SelectionSummary
              kind={kind}
              isLoading={isLoading}
              isError={isError}
              selectedCount={selectedCount}
              hiddenCount={hiddenCount}
              hasCalendarWarning={hasCalendarWarning}
            />
          </div>
          <div className='d-flex align-items-center zocks-gap-0.75 flex-shrink-0 ms-auto'>
            {disabled ? null : (
              <Button
                type='button'
                variant='grayButton'
                className={classNames('button-medium zocks-px-0.75 zocks-border-color-gray-500 text-nowrap', styles.cancelButton)}
                disabled={isSaving}
                onClick={onCancel}
              >
                Cancel
              </Button>
            )}
            <Button
              type='button'
              variant='primary'
              className='button-medium py-2 zocks-px-0.75 text-nowrap'
              disabled={!disabled && !isReady}
              loading={isSaving}
              onClick={disabled ? onCancel : () => void handleSave()}
            >
              {disabled ? 'Close' : 'Save changes'}
            </Button>
          </div>
        </>
      }
    />
  )
}
